import { useQuery } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from './onboarding-api'
import type { ConfirmedOrganizationClinic } from './organization-onboarding-contracts'

/**
 * One clinic's canonical Communications read and, when asked, its read-only
 * Twilio verification. Both are keyed by clinic and user, so every view of the
 * same clinic shares one request. A response for another clinic is ignored.
 * Mount only for an organization-authorized clinic.
 */
export function useCommunicationsStatus({
  tenantUid,
  fallbackStatus,
  apiClient,
  providerVerification = false,
  enabled = true,
  staleTime = 0,
}: {
  tenantUid: string
  fallbackStatus?: ConfirmedOrganizationClinic['communicationsStatus']
  apiClient?: OnboardingApiClient
  providerVerification?: boolean
  enabled?: boolean
  /** How long a result is reused before a new view asks again. */
  staleTime?: number
}) {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const api = apiClient ?? getOnboardingApiClient()
  const query = useQuery({
    queryKey: ['organization-communications', tenantUid, user?.uid ?? null],
    queryFn: () => api.getOrganizationCommunications!(user!, tenantUid),
    enabled: Boolean(
      enabled &&
      user &&
      (providerVerification || fallbackStatus !== 'ACTIVE') &&
      api.getOrganizationCommunications
    ),
    retry: false,
    staleTime,
  })
  const verification = useQuery({
    queryKey: ['organization-twilio', tenantUid, user?.uid ?? null],
    queryFn: () => api.verifyOrganizationTwilio!(user!, tenantUid),
    enabled: Boolean(
      enabled && providerVerification && user && api.verifyOrganizationTwilio
    ),
    retry: false,
    staleTime,
  })
  return {
    user,
    query,
    verification,
    // A cached or malformed response for another clinic cannot drive this view.
    state: query.data?.tenantUid === tenantUid ? query.data : undefined,
    verified:
      verification.data?.tenantUid === tenantUid
        ? verification.data
        : undefined,
  }
}
